// Peças que todas as seções do painel usam.
//
// Existem para que as telas fiquem sobre DADOS e não sobre marcação: uma
// tabela escrita à mão por seção é como sete telas divergem em espaçamento,
// em estado vazio e em como mostram erro.

import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { explicar } from "../api/problema";
import { Icone, type NomeDeIcone } from "../ui/Icone";

export function Cabecalho({
  titulo,
  descricao,
  acao,
}: {
  titulo: string;
  descricao?: string;
  acao?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl text-tinta">{titulo}</h1>
        {descricao && <p className="mt-0.5 text-sm text-tinta-fraca">{descricao}</p>}
      </div>
      {acao}
    </header>
  );
}

export function Botao({
  children,
  aoClicar,
  icone,
  tom = "primario",
  ocupado,
  tipo = "button",
  desabilitado,
}: {
  children: ReactNode;
  aoClicar?: () => void;
  icone?: NomeDeIcone;
  tom?: "primario" | "neutro" | "perigo";
  ocupado?: boolean;
  tipo?: "button" | "submit";
  // Separado de `ocupado` porque são coisas diferentes: ocupado é "espere",
  // desabilitado é "falta alguma coisa". Misturar os dois faria o botão
  // dizer que está trabalhando quando na verdade está recusando.
  desabilitado?: boolean;
}) {
  const cor = {
    primario: "bg-acao text-white",
    neutro: "border border-linha text-tinta hover:bg-fundo",
    perigo: "bg-perigo text-white",
  }[tom];
  return (
    <button
      type={tipo}
      onClick={aoClicar}
      disabled={ocupado || desabilitado}
      className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm disabled:opacity-60 ${cor}`}
    >
      {icone && <Icone nome={icone} tamanho={16} />}
      {ocupado ? "…" : children}
    </button>
  );
}

/** Estado de carga, erro e vazio — os três que toda lista precisa ter. */
export function Estado({
  consulta,
  vazio,
  children,
}: {
  consulta: { isPending: boolean; isError: boolean; error?: unknown };
  vazio?: boolean;
  children: ReactNode;
}) {
  if (consulta.isPending) {
    return <p className="py-10 text-center text-sm text-tinta-fraca">carregando…</p>;
  }
  if (consulta.isError) {
    return (
      <p className="flex items-center justify-center gap-2 py-10 text-sm text-perigo">
        <Icone nome="AlertTriangle" tamanho={16} />
        {explicar(consulta.error)}
      </p>
    );
  }
  if (vazio) {
    return <p className="py-10 text-center text-sm text-tinta-fraca">Nada aqui ainda.</p>;
  }
  return <>{children}</>;
}

export function Tabela({
  colunas,
  children,
  fixa,
  larguras,
}: {
  colunas: string[];
  children: ReactNode;
  /**
   * `table-layout: fixed`.
   *
   * O padrão (`auto`) dimensiona as colunas pelo CONTEÚDO, e com isso um
   * `max-w` ou um `truncate` dentro de uma célula não segura nada: a coluna
   * cresce até caber o texto e empurra as de depois para fora da área
   * visível. Foi o que escondeu os botões de liberar e descartar na
   * quarentena, atrás de um assunto de spam comprido.
   *
   * Só vale com `larguras`: fixo sem larguras divide tudo em partes iguais.
   */
  fixa?: boolean;
  /** Uma classe de largura do Tailwind por coluna, na mesma ordem. */
  larguras?: string[];
}) {
  return (
    // A tabela rola dentro do PRÓPRIO container: sem isto, uma coluna larga
    // empurra a página inteira para o lado e a barra lateral sai da tela.
    <div className="overflow-x-auto">
      <table className={`w-full text-sm ${fixa ? "table-fixed" : ""}`}>
        <thead>
          <tr className="border-b border-linha text-left text-xs text-tinta-fraca">
            {colunas.map((c, i) => (
              <th
                key={c || `col${i}`}
                className={`py-2 pr-3 font-medium whitespace-nowrap ${larguras?.[i] ?? ""}`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Linha({ children }: { children: ReactNode }) {
  return <tr className="border-b border-linha-suave hover:bg-fundo">{children}</tr>;
}

export function Celula({ children, largura }: { children: ReactNode; largura?: string }) {
  return (
    // `overflow-hidden` é o que torna o `truncate` de dentro efetivo: sem
    // ele o texto transborda a célula em vez de ganhar as reticências.
    <td className={`overflow-hidden py-3 pr-3 align-middle ${largura ?? ""}`}>
      {children}
    </td>
  );
}

/**
 * Um número grande com legenda. Só para o que o operador age em cima.
 *
 * `para` LEVA À LISTA QUE PRODUZIU O NÚMERO, já filtrada.
 *
 * A revisão de UX de 30/10/2026 apontou que os cartões da visão geral não
 * eram clicáveis, e o apontamento é maior que conveniência: um número só vale
 * se dá para ver DE QUE ele é feito. "Recusadas hoje: 12" sem caminho obriga
 * a pessoa a ir até Fila e entregas, escolher o tipo e a data à mão — e a
 * refazer de cabeça o filtro que o cartão já usou para contar. Quando os dois
 * divergem, é o número que parece errado.
 *
 * Cartão SEM destino continua existindo (o estado do serviço não tem lista),
 * e aí ele não finge ser clicável: nada de cursor, de realce, de foco.
 */
export function Indicador({
  rotulo,
  valor,
  detalhe,
  alerta,
  para,
}: {
  rotulo: string;
  valor: ReactNode;
  detalhe?: string;
  alerta?: boolean;
  /** Caminho da lista que detalha este número, com o filtro já embutido. */
  para?: string;
}) {
  const moldura = `block rounded-xl border bg-white p-4 ${
    alerta ? "border-aviso/50 bg-aviso/5" : "border-linha"
  }`;
  const miolo = (
    <>
      <p className="text-xs text-tinta-fraca">{rotulo}</p>
      <p
        className={`mt-1 text-2xl tabular-nums ${alerta ? "text-aviso" : "text-tinta"}`}
      >
        {valor}
      </p>
      {detalhe && <p className="mt-0.5 text-xs text-tinta-fraca">{detalhe}</p>}
    </>
  );

  if (!para) return <div className={moldura}>{miolo}</div>;

  return (
    <Link
      to={para}
      // O NOME ACESSÍVEL DIZ O DESTINO, não só o rótulo. Quem navega por
      // leitor de tela ouve uma lista de links: "Caixas" repetido oito vezes
      // não diz qual leva aonde.
      aria-label={`${rotulo} — ver a lista`}
      className={`${moldura} transition-shadow hover:border-acao/40 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao`}
    >
      {miolo}
      <span className="mt-2 flex items-center gap-1 text-xs text-acao">
        ver a lista
        <Icone nome="ChevronRight" tamanho={12} />
      </span>
    </Link>
  );
}

/** Pílula de estado: verde, âmbar ou vermelha. */
export function Selo({
  texto,
  tom,
}: {
  texto: string;
  tom: "ok" | "aviso" | "erro" | "neutro";
}) {
  const cor = {
    ok: "bg-acao-clara text-acao-escura",
    aviso: "bg-aviso/15 text-aviso",
    erro: "bg-perigo/15 text-perigo",
    neutro: "bg-linha text-tinta-media",
  }[tom];
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${cor}`}>{texto}</span>
  );
}

/**
 * O FILTRO DE UMA LISTA MORA NA URL.
 *
 * Nasceu de dois pedidos que são o mesmo problema: os cartões da visão geral
 * precisam LEVAR à lista já filtrada, e o suporte precisa poder mandar o
 * endereço da tela que está vendo ("é este aqui") em vez de descrever quais
 * caixinhas marcou.
 *
 * Com o filtro em `useState`, nenhum dos dois é possível: o cartão navega e a
 * tela abre sem filtro, e o endereço copiado não carrega o que está na tela.
 *
 * `replace: true` de propósito: digitar oito letras no campo de endereço
 * empilharia oito entradas no histórico, e aí o botão Voltar do navegador
 * apagaria uma letra por clique em vez de voltar à tela anterior.
 *
 * O valor ausente é `""`, nunca `null` — o parâmetro SAI da URL quando é
 * limpo, para `?tipo=&desde=` não ficar pendurado no endereço que a pessoa
 * vai colar.
 */
export function useFiltroNaURL<C extends string>(
  campos: readonly C[],
): readonly [Record<C, string>, (campo: C, valor: string) => void] {
  const [parametros, definirParametros] = useSearchParams();

  const valores = Object.fromEntries(
    campos.map((c) => [c, parametros.get(c) ?? ""]),
  ) as Record<C, string>;

  const definir = (campo: C, valor: string) => {
    definirParametros(
      (atuais) => {
        const novos = new URLSearchParams(atuais);
        if (valor) novos.set(campo, valor);
        else novos.delete(campo);
        return novos;
      },
      { replace: true },
    );
  };

  return [valores, definir] as const;
}

/**
 * O que os símbolos de uma coluna querem dizer.
 *
 * A revisão de UX apontou as colunas de DNS "sem estado". Metade do problema
 * era não haver estado antes de conferir; a outra metade é que ✓ e ⚠ sozinhos
 * não se explicam — e a diferença entre "ausente e opcional" e "ausente e é
 * problema" é justamente a que o operador precisa.
 */
export function Legenda({ itens }: { itens: { marca: ReactNode; diz: string }[] }) {
  return (
    <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-tinta-fraca">
      {itens.map((i) => (
        <div key={i.diz} className="flex items-center gap-1.5">
          <dt className="flex w-4 justify-center">{i.marca}</dt>
          <dd>{i.diz}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A página atual de uma lista.
 *
 * `reiniciarCom` é uma assinatura do filtro em vigor. Existe por um caso que
 * confunde: filtrar estando na página 3 deixa a pessoa numa página que o novo
 * filtro não tem, a tela aparece vazia, e ela conclui que a busca não achou
 * nada — quando o que faltou foi voltar para a primeira página.
 */
export function usePagina(reiniciarCom?: string) {
  const [pagina, setPagina] = useState(1);
  useEffect(() => setPagina(1), [reiniciarCom]);
  return [pagina, setPagina] as const;
}

/**
 * O rodapé de uma lista paginada.
 *
 * DEFEITO REAL: as listas do painel pediam 100 itens, não paginavam e não
 * mostravam o total. Com 101 caixas, uma sumia e ninguém ficava sabendo — a
 * tela não tinha como dizer que havia mais. A API sempre devolveu `total` e
 * `total_paginas`; era o painel que jogava fora.
 *
 * O TOTAL APARECE SEMPRE, mesmo numa página só. É a informação que o operador
 * veio buscar ("quantas caixas este cliente tem?"), e escondê-la quando cabe
 * numa página é escondê-la justamente quando é fácil de mostrar.
 */
export function Paginacao({
  pagina,
  dados,
  aoMudar,
  nome = "itens",
}: {
  pagina: number;
  dados?: { total?: number; por_pagina?: number; total_paginas?: number };
  aoMudar: (p: number) => void;
  /** Como chamar o que está listado, no plural. */
  nome?: string;
}) {
  const total = dados?.total ?? 0;
  const paginas = dados?.total_paginas ?? 1;
  const porPagina = dados?.por_pagina ?? 0;
  if (total === 0) return null;

  const primeiro = (pagina - 1) * porPagina + 1;
  const ultimo = Math.min(pagina * porPagina, total);

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-tinta-fraca">
      <span className="tabular-nums">
        {paginas > 1 ? `${primeiro}–${ultimo} de ` : ""}
        <strong className="text-tinta">{total}</strong> {nome}
      </span>
      {paginas > 1 && (
        <span className="flex items-center gap-1">
          <PassoDePagina rotulo="Anterior" icone="ChevronLeft"
            desligado={pagina <= 1} aoClicar={() => aoMudar(pagina - 1)} />
          <span className="px-2 tabular-nums">
            {pagina} de {paginas}
          </span>
          <PassoDePagina rotulo="Próxima" icone="ChevronRight"
            desligado={pagina >= paginas} aoClicar={() => aoMudar(pagina + 1)} />
        </span>
      )}
    </div>
  );
}

function PassoDePagina({
  rotulo,
  icone,
  desligado,
  aoClicar,
}: {
  rotulo: string;
  icone: NomeDeIcone;
  desligado: boolean;
  aoClicar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      disabled={desligado}
      aria-label={rotulo}
      title={rotulo}
      className="rounded-full border border-linha p-1.5 text-tinta-media disabled:opacity-30"
    >
      <Icone nome={icone} tamanho={14} />
    </button>
  );
}

/**
 * Seção prevista e ainda sem API.
 *
 * Aparece em vez de sumir do menu: quem opera precisa saber que o recurso
 * está previsto, e a alternativa é procurar onde ele está.
 */
export function AindaNao({ titulo, oQueFaria, quando }: { titulo: string; oQueFaria: string; quando: string }) {
  return (
    <>
      <Cabecalho titulo={titulo} />
      <div className="rounded-xl border border-linha bg-white p-8 text-center">
        <Icone nome="Package" tamanho={28} className="mx-auto text-tinta-fraca" />
        <p className="mt-3 text-sm text-tinta">{oQueFaria}</p>
        <p className="mt-1 text-sm text-tinta-fraca">{quando}</p>
      </div>
    </>
  );
}

/**
 * Confirmação DIGITADA para remoção.
 *
 * Não é um `confirm()` mais bonito. O que ela impede é diferente: um "OK" é
 * um reflexo — a mão já está no botão, e a caixa de diálogo aparece onde o
 * cursor ia clicar. Digitar o endereço obriga a LER qual é, e é o único
 * gesto que distingue "apagar esta" de "apagar a que eu achei que era".
 *
 * A frase é a mesma que o servidor exige em `?confirmar=`: a interface não
 * inventa proteção, ela expõe a que já existe na API. Se alguém chamar a
 * rota por fora, a exigência continua lá.
 */
export function ConfirmarRemocao({
  titulo,
  frase,
  oQueAcontece,
  rotulo = "Remover em definitivo",
  ocupado,
  erro,
  aoConfirmar,
  aoCancelar,
}: {
  titulo: string;
  /** O texto exato que a pessoa precisa digitar — e que vai em ?confirmar=. */
  frase: string;
  oQueAcontece: ReactNode;
  /**
   * O texto do botão. O padrão promete que a remoção não volta — e no caso da
   * CAIXA isso seria meia verdade contra o que o próprio diálogo explica
   * acima: a conta some, as mensagens ficam. Um botão que contradiz o aviso
   * ao lado ensina a não ler o aviso.
   */
  rotulo?: string;
  ocupado?: boolean;
  erro?: string;
  aoConfirmar: () => void;
  aoCancelar: () => void;
}) {
  const [digitado, setDigitado] = useState("");
  const confere = digitado.trim() === frase;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={aoCancelar}
    >
      <div
        className="w-full max-w-md rounded-xl border border-linha bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="flex items-center gap-2 text-base font-medium text-perigo">
          <Icone nome="AlertTriangle" tamanho={18} />
          {titulo}
        </h2>

        <div className="mt-3 space-y-2 text-sm text-tinta">{oQueAcontece}</div>

        <label className="mt-4 block text-xs text-tinta-fraca">
          Para confirmar, digite <code className="text-tinta">{frase}</code>
          <input
            autoFocus
            value={digitado}
            onChange={(e) => setDigitado(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && confere && !ocupado) aoConfirmar();
              if (e.key === "Escape") aoCancelar();
            }}
            // Nada de autocompletar nem corrigir: o navegador oferecendo o
            // endereço pronto devolveria o gesto reflexo que isto evita.
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm text-tinta"
          />
        </label>

        {erro && <p className="mt-2 text-sm text-perigo">{erro}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <Botao tom="neutro" aoClicar={aoCancelar}>
            Cancelar
          </Botao>
          <button
            type="button"
            disabled={!confere || ocupado}
            onClick={aoConfirmar}
            className="flex items-center gap-2 rounded-full bg-perigo px-4 py-2 text-sm text-white disabled:opacity-40"
          >
            {ocupado ? "…" : rotulo}
          </button>
        </div>
      </div>
    </div>
  );
}
